import { Typography } from '@mui/material';

import {
  getLicensingPublicConfig,
  hasPublishedNmlsNumbers,
} from '@/config/licensingEnv';
import { MarketingPageShell } from '@/components/Marketing/MarketingPageShell';
import { usePageMeta } from '@/hooks/usePageMeta';

/**
 * Licensing & regulatory disclosures. NMLS lines come from VITE_LICENSING_* env vars
 * after compliance sign-off.
 */
export default function LicensingPage() {
  const licensing = getLicensingPublicConfig();

  usePageMeta(
    'Licensing & disclosures',
    'Mortgage broker disclosures, NMLS consumer access, and contact information.'
  );
  return (
    <MarketingPageShell
      title="Licensing & disclosures"
      subtitle="Mortgage broker disclosures and how to verify our licensing."
    >
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 3, lineHeight: 1.85 }}
      >
        Unbeatable Loans provides mortgage brokering and related services in
        accordance with applicable federal and state law. We arrange loans with
        third-party lenders; we are not the lender. Loan approval and terms are
        subject to underwriting, appraisal, and program guidelines.
      </Typography>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Nationwide Mortgage Licensing System (NMLS)
      </Typography>
      {hasPublishedNmlsNumbers() ? (
        <Typography
          variant="body2"
          color="text.secondary"
          component="div"
          sx={{ mb: 2, lineHeight: 1.8 }}
        >
          {licensing.companyNmls && (
            <>
              Company NMLS: {licensing.companyNmls}
              <br />
            </>
          )}
          {licensing.mloNmlsLine && (
            <>
              {licensing.mloNmlsLine}
              <br />
            </>
          )}
          Verify status anytime at{' '}
          <a
            href="https://www.nmlsconsumeraccess.org/"
            target="_blank"
            rel="noopener noreferrer"
          >
            nmlsconsumeraccess.org
          </a>
          .
        </Typography>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, lineHeight: 1.8 }}
        >
          For the most current NMLS identification and state license status, use
          the NMLS Consumer Access portal at{' '}
          <a
            href="https://www.nmlsconsumeraccess.org/"
            target="_blank"
            rel="noopener noreferrer"
          >
            nmlsconsumeraccess.org
          </a>{' '}
          and search for Unbeatable Loans and your loan officer by name. Set{' '}
          <code>VITE_LICENSING_COMPANY_NMLS</code> and{' '}
          <code>VITE_LICENSING_MLO_LINE</code> in the frontend <code>.env</code>{' '}
          after compliance approves exact wording and numbers.
        </Typography>
      )}
      {licensing.additionalDisclosures ? (
        <Typography
          variant="body2"
          color="text.secondary"
          component="div"
          sx={{ mb: 2, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}
        >
          {licensing.additionalDisclosures}
        </Typography>
      ) : null}
      {licensing.complianceReviewedAt ? (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 2 }}
        >
          Disclosure content last reviewed: {licensing.complianceReviewedAt}
        </Typography>
      ) : (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 2 }}
        >
          Disclosure content: pending compliance review (no review date in env).
        </Typography>
      )}

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Equal housing opportunity
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, lineHeight: 1.8 }}
      >
        We are an equal opportunity provider. We do not discriminate on the
        basis of race, color, religion, national origin, sex, marital status,
        age, or any basis prohibited by law.
      </Typography>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Contact
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        component="div"
        sx={{ lineHeight: 1.8 }}
      >
        Unbeatable Loans
        <br />
        813 SW 16th Ct, Fort Lauderdale, FL 33315
        <br />
        Phone: <a href="tel:+19544661614">954-466-1614</a>
        <br />
        Email:{' '}
        <a href="mailto:support@unbeatableloans.com">
          support@unbeatableloans.com
        </a>
      </Typography>
    </MarketingPageShell>
  );
}
